import React, {
  useContext, useRef, useState, useEffect
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useClickOutside from 'Hooks/useClickOutside';
import useModal from 'Hooks/useModal';
import useShortcut from 'Hooks/useShortcut';
import { ThemeContext } from 'Context';
import { selectUserName, selectUserRole } from 'store/Faraday/selectors';
import { selectIsOpen } from 'store/SideBar/selectorContext';
import { selectUserEmail } from 'store/Settings/settingsSelectors';
import { selectWhatsNewVersionIds } from 'store/WhatsNew/selectors';
import { getWhatsNew } from 'store/WhatsNew/actions';
import { selectHasUnseenWhatsNew } from 'store/Preferences/whatsNew/selectors';
import Settings from 'Screens/Contextualization/Settings';
import ColorImage from 'Common/Components/ColorImage';
import UserDropdown from './Dropdown';
import Extras from './Modals/Extras';
import { isUserMenuNew } from './newFeatures';
import About from './Modals/About';
import Help from './Modals/Help';
import WhatsNew from './Modals/WhatsNew';
import {
  UserContainer,
  UserTrigger,
  AvatarSlot,
  UserInfo,
  UserName,
  UserInfoText
} from './styled';
import getRoleLabel from 'Common/Functions/getRoleLabel';

const SideBarUser = () => {
  const dispatch = useDispatch();
  const [ModalPreferences, showModalPreferences, toggleModalPreferences] =
    useModal(Settings);
  const [ModalExtras, showModalExtras, toggleModalExtras] = useModal(Extras);
  const [ModalAbout, showModalAbout, toggleModalAbout] = useModal(About);
  const [ModalHelp, showModalHelp, toggleModalHelp] = useModal(Help);
  const [ModalWhatsNew, showModalWhatsNew, toggleModalWhatsNew] = useModal(WhatsNew);

  const [openMenu, setOpenMenu] = useState(false);
  const dataRef = useRef();
  useClickOutside(dataRef, () => setOpenMenu(false));

  useEffect(() => {
    if (
      showModalPreferences ||
      showModalExtras ||
      showModalAbout ||
      showModalHelp ||
      showModalWhatsNew
    ) {
      setOpenMenu(false);
    }
  }, [showModalPreferences, showModalExtras, showModalAbout, showModalHelp, showModalWhatsNew]);

  useEffect(() => {
    dispatch(getWhatsNew());
  }, [dispatch]);

  const toggleMenu = () => {
    if (!openMenu) dispatch(getWhatsNew());
    setOpenMenu(!openMenu);
  };

  useShortcut('mod+,', toggleModalPreferences, { description: 'Open preferences' });

  const isOpen = useSelector(selectIsOpen);
  const userRole = useSelector(selectUserRole);
  const userName = useSelector(selectUserName);
  const userEmail = useSelector(selectUserEmail);
  const roleLabel = getRoleLabel(userRole);

  const { themeStyles } = useContext(ThemeContext);
  const whatsNewVersionIds = useSelector(selectWhatsNewVersionIds);
  const hasUnseenWhatsNew = useSelector(selectHasUnseenWhatsNew(whatsNewVersionIds));

  const modals = {
    Extras: {
      Modal: ModalExtras,
      showModal: showModalExtras,
      toggleModal: toggleModalExtras
    },
    About: {
      Modal: ModalAbout,
      showModal: showModalAbout,
      toggleModal: toggleModalAbout
    },
    Help: {
      Modal: ModalHelp,
      showModal: showModalHelp,
      toggleModal: toggleModalHelp
    },
    WhatsNew: {
      Modal: ModalWhatsNew,
      showModal: showModalWhatsNew,
      toggleModal: toggleModalWhatsNew,
      hasUnseen: hasUnseenWhatsNew,
      isAvailable: whatsNewVersionIds.length > 0
    },
    Preferences: {
      Modal: ModalPreferences,
      showModal: showModalPreferences,
      toggleModal: toggleModalPreferences
    }
  };

  return (
    <UserContainer ref={ dataRef }>
      <UserTrigger onClick={ toggleMenu } title={ userName }>
        <AvatarSlot>
          <ColorImage
            title={ userName }
            role={ userRole }
            hasIndicator={ hasUnseenWhatsNew || isUserMenuNew() }
            indicatorBorderColor={ themeStyles.backgrounds.sidebar }
          />
        </AvatarSlot>
        {isOpen && (
          <UserInfo>
            <UserName children={ userName } />
            <UserInfoText children={ roleLabel } />
            <UserInfoText children={ userEmail } />
          </UserInfo>
        )}
        {openMenu && (
          <UserDropdown modals={ modals } closeMenu={ () => setOpenMenu(false) } />
        )}
      </UserTrigger>
      {modals.Preferences.showModal && (
        <modals.Preferences.Modal onClose={ modals.Preferences.toggleModal } />
      )}
      {modals.Extras.showModal && (
        <modals.Extras.Modal onClose={ modals.Extras.toggleModal } />
      )}
      {modals.About.showModal && (
        <modals.About.Modal onClose={ modals.About.toggleModal } />
      )}
      {modals.Help.showModal && (
        <modals.Help.Modal onClose={ modals.Help.toggleModal } />
      )}
      {modals.WhatsNew.showModal && (
        <modals.WhatsNew.Modal onClose={ modals.WhatsNew.toggleModal } />
      )}
    </UserContainer>
  );
};

export default SideBarUser;
